/**
 * Analytics Portal V1 — shared ranking list (Phase 9, plan §12.2).
 *
 * Consumes `rankings = {mode: {rows[], excluded_entities}}`. Rows deep-link into the matching
 * entity tab (the URL carries entity + range), and the `excluded_entities` count is surfaced
 * honestly rather than silently dropping entities whose unique inputs were unavailable.
 */
import React, { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { ChartCard } from "./ChartCard";
import { EmptyState } from "./EmptyState";

const MODES = [
  { key: "most_viewed", label: "Most viewed", value: (r) => r.visits, unit: "visits" },
  { key: "fastest_growing", label: "Fastest growing", value: (r) => r.growth_pct, unit: "%",
    context: (r) => (r.previous_visits !== null && r.previous_visits !== undefined
      ? `${r.visits} visits vs ${r.previous_visits} previously` : null) },
  { key: "most_engaged", label: "Most engaged", value: (r) => r.engagement_rate, unit: "%",
    note: "Rates are shown with the unique-visitor count they are based on, so a small audience is visible rather than hidden.",
    context: (r) => (r.unique_visitors !== null && r.unique_visitors !== undefined
      ? `${r.engagement_actions} actions from ${r.unique_visitors} unique visitors` : null) },
  { key: "most_converted", label: "Most converted", value: (r) => r.conversion_rate, unit: "%",
    note: "Rates are shown with the unique-visitor count they are based on, so a small audience is visible rather than hidden.",
    context: (r) => (r.unique_visitors !== null && r.unique_visitors !== undefined
      ? `${r.unique_converters} converters from ${r.unique_visitors} unique visitors` : null) },
];

const TAB_FOR_TYPE = { personal: "profile", business: "business", capital: "capital",
                       event: "events", resource: "resources" };

export const RankingTable = ({ rankings, onOpenEntity }) => {
  const [mode, setMode] = useState("most_viewed");
  if (!rankings || !Object.keys(rankings).length) return null;
  const active = MODES.find((m) => m.key === mode) || MODES[0];
  const block = rankings[mode] || { rows: [], excluded_entities: 0 };
  const rows = block.rows || [];

  return (
    <ChartCard title="Your entities" description="Ranked across everything you manage."
               testId={`analytics-ranking-${mode}`}
               summary={rows.map((r, i) => `${i + 1}. ${r.name}: ${active.value(r) ?? "n/a"}`).join("; ")}>
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Ranking mode">
        {MODES.map((option) => (
          <button
            key={option.key} type="button" role="tab" aria-selected={option.key === mode}
            onClick={() => setMode(option.key)}
            className={`rounded-full border px-3 py-1 text-xs font-[560] transition-colors ${
              option.key === mode
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card text-muted-foreground hover:text-foreground"}`}
            data-testid={`analytics-ranking-mode-${option.key}`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {!rows.length ? (
        <div className="mt-4"><EmptyState state="no-data-in-period" /></div>
      ) : (
        <ul className="mt-4 divide-y divide-border">
          {rows.map((row, index) => (
            <li key={`${row.entity_type}-${row.entity_id}`} className="py-2.5">
              <button
                type="button"
                onClick={() => onOpenEntity?.(TAB_FOR_TYPE[row.entity_type], row.entity_id)}
                className="group flex w-full items-center justify-between gap-3 text-left"
                data-testid={`analytics-ranking-row-${row.entity_id}`}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <span className="w-5 shrink-0 text-xs tabular-nums text-muted-foreground"
                        aria-hidden="true">{index + 1}</span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 truncate text-sm font-[560] text-foreground">
                      {row.name || "Untitled"}
                      {row.archived && (
                        <span className="rounded-full border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
                              data-testid={`analytics-ranking-archived-${row.entity_id}`}>
                          Archived
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      <span className="capitalize">{row.entity_type}</span>
                      {active.context?.(row) ? ` · ${active.context(row)}` : ""}
                    </span>
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2 tabular-nums text-sm text-foreground">
                  {active.value(row) ?? "—"}{active.value(row) !== null && active.value(row) !== undefined
                    ? (active.unit === "%" ? "%" : ` ${active.unit}`) : ""}
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {active.note && rows.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground"
           data-testid={`analytics-ranking-note-${mode}`}>{active.note}</p>
      )}

      {block.excluded_entities > 0 && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="analytics-ranking-excluded">
          {block.excluded_entities} {block.excluded_entities === 1 ? "entity is" : "entities are"} not
          ranked here because exact unique data isn&apos;t available for the selected period.
        </p>
      )}
    </ChartCard>
  );
};
